import { createQuery } from "@tanstack/solid-query";
import { User } from "stoat.js";

import { useClient } from ".";

/**
 * Create a new resource for current account's MFA configuration
 * @returns MFA configuration
 */
export function createMfaResource() {
  const client = useClient();

  return createQuery(() => ({
    queryKey: ["mfa", client().user!.id],
    queryFn: () => client().account.mfa(),
    throwOnError: true,
  }));
}

/**
 * Create a new resource for user profile
 * @param user User
 * @returns User profile resource
 */
export function createProfileResource(user: User) {
  return createQuery(() => ({
    queryKey: ["profile", user.id],
    queryFn: () => user!.fetchProfile(),
    throwOnError: true,
  }));
}

/**
 * Create a new resource for own user profile
 * @returns User profile resource
 */
export function createOwnProfileResource() {
  const client = useClient();
  return createProfileResource(client().user!);
}

/**
 * Probe whether the current session has admin privileges. Backed by a lightweight
 * GET against the cleanest admin-namespace endpoint; 200 = admin, anything else = not.
 * Single shared query (`["admin-probe", userId]`) so sidebar entries and the
 * `RequireAdmin` route guard reuse one network call.
 */
export function createAdminProbeResource() {
  const client = useClient();
  return createQuery(() => ({
    queryKey: ["admin-probe", client().user?.id ?? "anon"],
    queryFn: async () => {
      try {
        const [authHeader, authValue] = client().authenticationHeader;
        const resp = await fetch("/api/admin/safety/reports?limit=1", {
          headers: {
            Accept: "application/json",
            [authHeader]: authValue,
          },
        });
        return resp.ok;
      } catch {
        return false;
      }
    },
    staleTime: Infinity,
    retry: false,
  }));
}

/**
 * Create a new resource for own bots
 * @returns List of owned bots
 */
export function createOwnBotsResource() {
  const client = useClient();
  return createQuery(() => ({
    queryKey: ["bots", client().user!.id],
    queryFn: () => client().bots.fetchOwned(),
    throwOnError: true,
  }));
}
